// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Anmol Rajpal <anmol@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

import { useState, useRef, useEffect } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Building2, Activity, Users, Settings, ChevronLeft, ChevronRight, LogOut } from 'lucide-react'
import { useAuth } from '@/context/auth-context'
import { PersonAvatar } from '@/components/ui/person-avatar'
import { Branding } from '@qh/design-tokens/branding'

export default function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { t } = useTranslation()
  const { user, isPlatform, signOutUser } = useAuth()
  const [collapsed, setCollapsed] = useState(false)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const navigate = useNavigate()
  const menuRef = useRef<HTMLDivElement>(null)

  const email = user?.email ?? ''
  const displayName = user?.displayName?.trim() || email || t('nav.operatorFallback')

  async function handleLogout() {
    try {
      await signOutUser()
    } finally {
      navigate('/login', { replace: true })
    }
  }

  const NAV_LINKS = [
    { to: '/orgs', label: t('nav.orgs'), icon: Building2 },
    { to: '/usage', label: t('nav.usage'), icon: Activity },
    { to: '/team', label: t('nav.team'), icon: Users },
    ...(isPlatform ? [{ to: '/settings', label: t('nav.settings'), icon: Settings }] : []),
  ]

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowUserMenu(false)
      }
    }
    if (showUserMenu) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showUserMenu])

  return (
    <aside
      className={[
        'h-full flex flex-col shrink-0 relative',
        'bg-gradient-to-b from-[#012031] via-[#033C85] to-[#006EF9]',
        'transition-all duration-300 ease-in-out',
        collapsed ? 'w-16' : 'w-60',
      ].join(' ')}
    >
      {/* Decorative orbs — contained so they don't clip the popup */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -bottom-16 -right-16 w-[220px] h-[220px] rounded-full bg-[#02C7C3]/15 blur-3xl" />
        <div className="absolute top-1/3 -left-10 w-[140px] h-[140px] rounded-full bg-white/5 blur-2xl" />
      </div>

      {/* Collapse toggle */}
      <div
        className={[
          'flex items-center border-b border-white/10 relative z-10 py-3',
          collapsed ? 'justify-center px-3' : 'justify-end px-3',
        ].join(' ')}
      >
        <button
          onClick={() => setCollapsed(c => !c)}
          className="h-7 w-7 rounded-lg bg-white/10 hover:bg-white/20 transition-colors flex items-center justify-center text-white shrink-0"
          aria-label={collapsed ? t('nav.expandSidebar') : t('nav.collapseSidebar')}
        >
          {collapsed ? (
            <ChevronRight className="h-3.5 w-3.5" />
          ) : (
            <ChevronLeft className="h-3.5 w-3.5" />
          )}
        </button>
      </div>

      {/* Nav links — scrolls independently */}
      <nav className="flex-1 overflow-y-auto px-3 py-4 flex flex-col gap-1 relative z-10">
        {NAV_LINKS.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            onClick={onNavigate}
            title={collapsed ? label : undefined}
            className={({ isActive }) =>
              [
                'flex items-center rounded-xl text-sm font-medium transition-colors whitespace-nowrap',
                collapsed ? 'justify-center py-2.5 px-0' : 'gap-3 px-3 py-2.5',
                isActive
                  ? 'bg-white/15 text-white'
                  : 'text-white/65 hover:bg-white/10 hover:text-white',
              ].join(' ')
            }
          >
            <Icon className="h-4 w-4 shrink-0" />
            {!collapsed && label}
          </NavLink>
        ))}
      </nav>

      {/* User / logout — sticky at bottom */}
      <div className="shrink-0 px-3 py-4 border-t border-white/10 relative z-10" ref={menuRef}>
        {showUserMenu && (
          <div className="absolute bottom-full left-2 right-2 mb-2 bg-card rounded-2xl border border-border shadow-2xl overflow-hidden z-50">
            {!collapsed && (
              <div className="px-4 py-3 border-b border-border">
                <p className="text-sm font-semibold text-foreground truncate">{displayName}</p>
                {email && <p className="text-xs text-muted-foreground truncate">{email}</p>}
              </div>
            )}
            <button
              onClick={() => void handleLogout()}
              className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-destructive hover:bg-destructive/5 transition-colors"
            >
              <LogOut className="h-4 w-4 shrink-0" />
              {!collapsed && t('nav.logOut')}
            </button>
          </div>
        )}

        <button
          onClick={() => setShowUserMenu(v => !v)}
          className={[
            'flex items-center rounded-xl hover:bg-white/10 transition-colors w-full',
            collapsed ? 'justify-center px-0 py-2' : 'gap-3 px-2 py-2',
          ].join(' ')}
          title={collapsed ? displayName : undefined}
        >
          <PersonAvatar seed={user?.uid || email || displayName} size={36} ringClassName="ring-white/30" />
          {!collapsed && (
            <div className="flex flex-col min-w-0 text-left">
              <span className="text-sm font-medium text-white truncate">{displayName}</span>
              {email && <span className="text-xs font-medium text-[#02C7C3] truncate">{email}</span>}
            </div>
          )}
        </button>
        <Branding
          variant="inverse"
          compact={collapsed}
          className={collapsed ? 'mt-2 text-[13px]' : 'mt-3 border-t border-white/10 pt-3 text-[11px]'}
        />
      </div>
    </aside>
  )
}
